import { useCallback, useEffect, useState } from 'react';
import AnnualReportService from '../Services/annualReport.service';
import { ProfitAndLossSource, readReportDateBasis } from '../Components/AnnualReport/annualReportUtils';

/** The P&L a report starts from. */
export const PL_SOURCE = {
    sortCode: 'sortCode',
    detailed: 'detailed',
    final: 'final',
};

const VALID_SOURCES = new Set(Object.values(PL_SOURCE));

export const plSourceToApiSource = (source) =>
    (source === PL_SOURCE.sortCode ? ProfitAndLossSource.SortCodeReport : ProfitAndLossSource.LedgerTrialBalance);

/** The live report the final P&L was fetched from. */
export const apiSourceToPlSource = (sourceType) =>
    (sourceType === ProfitAndLossSource.SortCodeReport ? PL_SOURCE.sortCode : PL_SOURCE.detailed);

const CHANGE_EVENT = 'pl-source-change';

const storageKey = (caseId, year) => `annualReport:plSource:${caseId}:${year}`;

const readChoice = (caseId, year) => {
    if (!caseId || !year) return null;
    try {
        const stored = localStorage.getItem(storageKey(caseId, year));
        return VALID_SOURCES.has(stored) ? stored : null;
    } catch {
        return null;
    }
};

const resolveSource = (choice, { defaultSource, finalAvailable, allowDetailed }) => {
    let source = choice ?? defaultSource;
    if (source === PL_SOURCE.final && !finalAvailable) {
        source = defaultSource === PL_SOURCE.final ? PL_SOURCE.detailed : defaultSource;
    }
    if (source === PL_SOURCE.detailed && !allowDetailed) source = PL_SOURCE.sortCode;
    return source;
};

/**
 * The P&L source of a case and year: דוח רווח והפסד לפי קוד מיון, דוח רווח והפסד מפורט, or the final
 * annual-report P&L (דוח רווח והפסד סופי) once it was fetched. The choice is shared per case and year, so
 * Form 6111, the matching report and the annual report's תיאומים start from the same profit.
 *
 * - `includeLive`: true, false or 'whenFinal' — read the final P&L's own source again for per-line differences.
 * - `liveFollowsSource`: always read the selected live report (or the final's source when final is selected).
 */
export default function useProfitAndLossSource({
    caseId,
    year,
    token,
    enabled = true,
    defaultSource = PL_SOURCE.sortCode,
    allowDetailed = true,
    includeLive = false,
    liveFollowsSource = false,
}) {
    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [choice, setChoice] = useState(() => readChoice(caseId, year));

    useEffect(() => {
        setChoice(readChoice(caseId, year));
        const onChange = (event) => {
            if (event.detail?.key === storageKey(caseId, year)) setChoice(event.detail.value);
        };
        window.addEventListener(CHANGE_EVENT, onChange);
        return () => window.removeEventListener(CHANGE_EVENT, onChange);
    }, [caseId, year]);

    const eligible = Boolean(comparison?.eligible);
    const finalAvailable = Boolean(eligible && comparison?.exists);
    const source = resolveSource(choice, { defaultSource, finalAvailable, allowDetailed });

    const requestLive = liveFollowsSource
        || (includeLive === 'whenFinal' ? source === PL_SOURCE.final : Boolean(includeLive));
    const requestLiveSource = liveFollowsSource && source !== PL_SOURCE.final ? plSourceToApiSource(source) : null;
    // A live report is read with the date basis its page shows; the final P&L's own source keeps the saved one.
    const requestDateBasis = requestLiveSource === null ? null : readReportDateBasis(requestLiveSource);

    useEffect(() => {
        if (!enabled || !caseId || !year || !token) {
            setComparison(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        AnnualReportService.getProfitAndLossComparison(caseId, year, requestLive, token, requestLiveSource, requestDateBasis)
            .then((data) => {
                if (cancelled) return;
                setComparison(data);
                setError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                setComparison(null);
                setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [caseId, year, token, requestLive, requestLiveSource, requestDateBasis, enabled, reloadKey]);

    const setSource = useCallback((value) => {
        if (!VALID_SOURCES.has(value)) return;
        setChoice(value);
        if (!caseId || !year) return;
        const key = storageKey(caseId, year);
        try {
            localStorage.setItem(key, value);
        } catch {
            // Private mode: the choice lasts for this page only.
        }
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key, value } }));
    }, [caseId, year]);

    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    return {
        source,
        setSource,
        /** The comparison response for eligible cases (lines of the live report even without a final P&L). */
        comparison: eligible ? comparison : null,
        /** The final P&L, or null when it was not fetched (or the case has no annual report). */
        finalComparison: finalAvailable ? comparison : null,
        eligible,
        finalAvailable,
        loading,
        error,
        reload,
    };
}
